import { library } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faClock, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { CurrentWorkspaceSelectActionContainer } from '../../../../modules/workspaces/currentWorkspace/ui/selectAction/CurrentWorkspaceSelectAction.container';
import { CurrentWorkspaceSelectLinkTableContainer } from '../../../../modules/workspaces/currentWorkspace/ui/selectLinkInTable/CurrentWorkspaceSelectLinkInTable.container';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateString } from '../../../helpers/date.helpers';
import { GetTimeZoneForOffset } from '../../../models/timeZones/timeZones.model';
import { useWorkspacePermissions } from '../workspaces.permissions';
import { IWorkspaceListItem } from '../workspaces.store';

library.add(faUser, faCheck, faClock);

const getBoldRenderer = (currentWorkspaceId: string, selector: (d: IWorkspaceListItem) => ReactElement): ((data: IWorkspaceListItem) => ReactElement) => {
  return (data: IWorkspaceListItem) => {
    const isBold = data.id === currentWorkspaceId;
    const value = selector(data);
    const content = value !== null ? value : ' - ';
    return <span className={isBold ? 'bold-text' : ''}>{content}</span>;
  };
};

interface IWorkspaceListProps {
  isLoading: boolean;
  currentWorkspaceId: string;
  workspaces: IWorkspaceListItem[];
  sorting: {
    orderBy: string;
    orderType: string;
  };
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
  toggleRenameWorkspaceOnList: (workspaceId: string) => void;
  toggleChangeOwnerOnList: (workspaceId: string) => void;
  toggleDeleteWorkspaceOnList: (workspaceId: string) => void;
  toggleUpdateWorkspaceTimeZoneOnList: (workspaceId: string, timeZoneOffset: number) => void;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
}

export const WorkspacesList = observer((props: IWorkspaceListProps): ReactElement => {
  const { t } = useTranslator();
  const workspacePermissions = useWorkspacePermissions();

  const getOnToggleTimeZoneUpdateCallback = (id: string, timeZoneOffset: number, callback: (id: string, timeZoneOffset: number) => void) => (): void => {
    callback(id, timeZoneOffset);
  };

  const nameRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => (
    <CurrentWorkspaceSelectLinkTableContainer
      workspaceId={workspace.id}
      name={workspace.name}
      isEncrypted={workspace.isEncrypted}
      isLocked={workspace.isLocked}
      isMy={workspace.isOwner}
    />
  ));

  const roleRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => <span>{workspace.role}</span>);
  const createdDateRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => (
    <>{toLocaleDateString(workspace.createdDate.toString())}</>
  ));
  const ownerRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => <>{workspace.owner}</>);
  const usersCountRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) =>
    workspace.role === 'Worker' ? (
      <span> - </span>
    ) : (
      <span
        title={t('workers_users_count_tooltip', {
          workersCount: workspace.usersAndWorkersCount.workersCount,
          usersCount: workspace.usersAndWorkersCount.usersCount,
        })}
      >
        {workspace.usersAndWorkersCount.usersCount} + {workspace.usersAndWorkersCount.workersCount}
      </span>
    )
  );
  const datasetsCountRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => <>{workspace.datasetsCount}</>);
  const imagesCountRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => <>{workspace.imagesCount}</>);
  const projectsCountRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => <>{workspace.projectsCount}</>);
  const timeZoneRenderer = getBoldRenderer(props.currentWorkspaceId, (workspace: IWorkspaceListItem) => <>{GetTimeZoneForOffset(workspace.timeZoneOffset)}</>);

  const optionsRenderer = (workspace: IWorkspaceListItem): ReactElement => {
    return (
      <>
        <CurrentWorkspaceSelectActionContainer workspaceId={workspace.id} />
        {workspace.isOwner && (
          <>
            <IconButton
              color='primary'
              size='sm'
              onClick={() => {
                props.toggleRenameWorkspaceOnList(workspace.id);
              }}
              title={t('rename')}
            >
              <EditIcon />
            </IconButton>
            <EnableFor hasPermission={workspacePermissions.canChangeOwner()}>
              <IconButton
                color='primary'
                size='sm'
                onClick={() => {
                  props.toggleChangeOwnerOnList(workspace.id);
                }}
                title={t('change_owner')}
              >
                <FontAwesomeIcon icon={'user'} />
              </IconButton>
            </EnableFor>
            <IconButton
              color='primary'
              size='sm'
              onClick={getOnToggleTimeZoneUpdateCallback(workspace.id, workspace.timeZoneOffset, props.toggleUpdateWorkspaceTimeZoneOnList)}
              title={t('update_workspace_time_zone')}
            >
              <FontAwesomeIcon icon={'clock'} />
            </IconButton>
            <EnableFor hasPermission={workspacePermissions.canDelete()}>
              <IconButton
                size='sm'
                onClick={() => {
                  props.toggleDeleteWorkspaceOnList(workspace.id);
                }}
                title={t('delete')}
              >
                <TrashIcon />
              </IconButton>
            </EnableFor>
          </>
        )}
      </>
    );
  };

  const gColumns: IGColumnProps<IWorkspaceListItem>[] = [
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: nameRenderer,
    },
    {
      sortBy: 'role',
      headerName: t('my_role'),
      renderer: roleRenderer,
    },
    {
      sortBy: 'createdDate',
      headerName: t('created_date'),
      renderer: createdDateRenderer,
    },
    {
      sortBy: 'owner',
      headerName: t('owner'),
      renderer: ownerRenderer,
    },
    {
      sortBy: 'usersAndWorkersCount',
      headerName: t('workers_users_count'),
      renderer: usersCountRenderer,
    },
    {
      sortBy: 'datasetsCount',
      headerName: t('datasets_count'),
      renderer: datasetsCountRenderer,
    },
    {
      sortBy: 'imagesCount',
      headerName: t('images_count'),
      renderer: imagesCountRenderer,
    },
    {
      sortBy: 'projectsCount',
      headerName: t('projects_count'),
      renderer: projectsCountRenderer,
    },
    {
      sortBy: 'timeZoneOffset',
      headerName: t('time_zone'),
      renderer: timeZoneRenderer,
    },
    {
      headerName: t('actions'),
      renderer: optionsRenderer,
      actions: true,
      width: 'min-content',
    },
  ];

  return (
    <GTable<IWorkspaceListItem>
      isLoading={props.isLoading}
      columns={gColumns}
      className='workspaces-table'
      items={props.workspaces}
      noItemsPlaceholder=''
      paginationProps={{
        pageNumber: props.pagination.pageNumber,
        pageSize: props.pagination.pageSize,
        totalCount: props.pagination.totalCount,
        onChange: props.onPaginationChange,
      }}
      sortingModel={[props.sorting]}
      onSortChanged={props.onOrderingChange}
    />
  );
});
